import React, { ReactNode } from 'react';
import { LocalDate } from '@js-joda/core';
import AntdCalendar from 'antd/lib/calendar';
import moment, { Moment } from 'moment';

/**
 * {@docCategory Calendar}
 */
export interface CalendarProps {
   /**
    * Customize the display of the date cell, the returned content will be appended to the cell
    */
   dateCellRenderer?: (date: LocalDate) => ReactNode;

   /**
    * Customize the display of the date cell, the returned content will override the cell
    */
   dateFullCellRenderer?: (date: LocalDate) => ReactNode;

   /**
    * The date selected by default
    */
   defaultValue?: LocalDate;

   /**
    * Function that specifies the dates that cannot be selected
    */
   disabledDate?: (date: LocalDate) => boolean;

   /**
    * Whether to display in full-screen
    * @defaultvalue true
    */
   fullscreen?: boolean;

   /**
    * Render custom header in panel
    */
   headerRenderer?: () => ReactNode;

   /**
    * Specifies the dates that cannot be selected
    */
   maxDate?: LocalDate;

   /**
    * Specifies the dates that cannot be selected
    */
   minDate?: LocalDate;

   /**
    * The display mode of the calendar
    * @defaultvalue 'month'
    */
   mode?: 'month' | 'year';

   /**
    * Customize the display of the month cell, the returned content will be appended to the cell
    */
   monthCellRenderer?: (date: LocalDate) => ReactNode;

   /**
    * Customize the display of the month cell, the returned content will override the cell
    */
   monthFullCellRenderer?: (date: LocalDate) => ReactNode;

   /**
    * To set valid range
    */
   validRange?: [LocalDate, LocalDate];

   /**
    * The current selected date
    */
   value?: LocalDate;

   /**
    * Callback for when date changes
    */
   onChange?: (date: LocalDate) => void;

   /**
    * Callback for when panel changes
    */
   onPanelChange?: (date: LocalDate, mode: string) => void;

   /**
    * Callback for when a date is selected
    */
   onSelect?: (date: LocalDate) => void;
}

export const Calendar = (props: CalendarProps) => {
   return (
      <AntdCalendar
         dateCellRender={dateCellRenderer(props)}
         dateFullCellRender={dateFullCellRenderer(props)}
         defaultValue={defaultValue(props)}
         disabledDate={disabledDate(props)}
         fullscreen={props.fullscreen}
         headerRender={props.headerRenderer}
         mode={props.mode}
         monthCellRender={monthCellRenderer(props)}
         monthFullCellRender={monthFullCellRenderer(props)}
         validRange={validRange(props)}
         value={value(props)}
         onChange={onChange(props)}
         onPanelChange={onPanelChange(props)}
         onSelect={onSelect(props)}
      />
   );
};

const dateCellRenderer = (props: CalendarProps) => {
   if (props.dateCellRenderer) {
      return (date: Moment): ReactNode => {
         if (props.dateCellRenderer) {
            return props.dateCellRenderer(convertMomentToLocalDate(date));
         }
      }
   }

   return undefined;
}

const dateFullCellRenderer = (props: CalendarProps) => {
   if (props.dateFullCellRenderer) {
      return (date: Moment): ReactNode => {
         if (props.dateFullCellRenderer) {
            return props.dateFullCellRenderer(convertMomentToLocalDate(date));
         }
      }
   }

   return undefined;
}

const monthCellRenderer = (props: CalendarProps) => {
   if (props.monthCellRenderer) {
      return (date: Moment): ReactNode => {
         if (props.monthCellRenderer) {
            return props.monthCellRenderer(convertMomentToLocalDate(date));
         }
      }
   }

   return undefined;
}

const monthFullCellRenderer = (props: CalendarProps) => {
   if (props.monthFullCellRenderer) {
      return (date: Moment): ReactNode => {
         if (props.monthFullCellRenderer) {
            return props.monthFullCellRenderer(convertMomentToLocalDate(date));
         }
      }
   }

   return undefined;
}

const validRange = (props: CalendarProps): [Moment, Moment] | undefined => {
   if (props.validRange) {
      return [convertLocalDateToMoment(props.validRange[0]),
              convertLocalDateToMoment(props.validRange[1])];
   }

   return undefined;
};

const defaultValue = (props: CalendarProps): Moment | undefined => {
   if (props.defaultValue) {
      return convertLocalDateToMoment(props.defaultValue);
   }

   return undefined;
};

const value = (props: CalendarProps): Moment | undefined => {
   if (props.value) {
      return convertLocalDateToMoment(props.value);
   }

   return undefined;
};

const disabledDate = (props: CalendarProps) => {
   if (props.disabledDate) {
      return (date: Moment) => {
         if (props.disabledDate) {
            props.disabledDate(convertMomentToLocalDate(date));
         }

         return false;
      }
   }

   if (props.minDate && props.maxDate) {
      return (date: Moment) => {
         if (props.minDate && props.maxDate) {
            return !date.isBetween(convertLocalDateToMoment(props.minDate),
                                   convertLocalDateToMoment(props.maxDate));
         }

         return false;
      }
   }

   if (props.minDate || props.maxDate) {
      return (date: Moment) => {
         if (props.minDate) {
            return date.isBefore(convertLocalDateToMoment(props.minDate));
         }

         if (props.maxDate) {
            return date.isAfter(convertLocalDateToMoment(props.maxDate));
         }

         return false;
      }
   }

   return undefined;
}

const onChange = (props: CalendarProps) => {
   if (props.onChange) {
      return (date: Moment) => {
         if (props.onChange) {
            props.onChange(convertMomentToLocalDate(date));
         }
      }
   }

   return undefined;
}

const onPanelChange = (props: CalendarProps) => {
   if (props.onPanelChange) {
      return (date: Moment, mode: string) => {
         if (props.onPanelChange) {
            props.onPanelChange(convertMomentToLocalDate(date), mode);
         }
      }
   }

   return undefined;
}

const onSelect = (props: CalendarProps) => {
   if (props.onSelect) {
      return (date: Moment) => {
         if (props.onSelect) {
            props.onSelect(convertMomentToLocalDate(date));
         }
      }
   }

   return undefined;
}

const convertMomentToLocalDate = (date: Moment): LocalDate => {
   return LocalDate.of(date.year(), date.month() + 1, date.date());
};

const convertLocalDateToMoment = (date: LocalDate): Moment => {
   return moment.utc([date.year(),
                      date.monthValue() - 1,
                      date.dayOfMonth()]);
};
